import { DEFAULT_CANVAS_CONFIG } from '../constants/canvasDefaults';

/**
 * Get a config value from a preset, falling back to defaultValue.
 * All presets are now v2 (full-resolution values) — no scaling needed.
 * Migration 005 converted all v1 presets to v2 in the database.
 */
export const getConfigValue = (preset, key, defaultValue) => {
  return preset?.config?.[key] ?? defaultValue;
};

/**
 * Wait for browser fonts before measuring Konva text. Konva.Text.height()
 * depends on the active font metrics, so measuring before web fonts load can
 * place the divider and second language using fallback-font dimensions.
 */
export const waitForCanvasFonts = async () => {
  if (!document.fonts?.ready || !document.fonts?.load) return;
  // Explicitly request every font the canvas can use. `document.fonts.ready`
  // alone only waits for fonts already in use by the DOM, whereas Konva draws
  // to its own canvas and otherwise may measure a fallback font on first mount.
  const canvasFonts = [
    'normal 54px "Futura"',
    'italic 54px "Futura"',
    'normal 54px "Didact Gothic"',
    'normal 54px "Questrial"',
  ];
  await Promise.race([
    Promise.all([
      document.fonts.ready,
      ...canvasFonts.map((font) => document.fonts.load(font)),
    ]),
    new Promise((resolve) => setTimeout(resolve, 4000)),
  ]);
};

/**
 * Calculate text width based on preset config.
 */
export const calculateTextWidth = (preset, canvasSize) => {
  const widthMode = getConfigValue(preset, 'textWidthMode', 'fixed');
  const textWidth = getConfigValue(preset, 'textWidth', DEFAULT_CANVAS_CONFIG.widthShort);
  const textWidthPercentage = getConfigValue(preset, 'textWidthPercentage', 75);
  const maxWidth = getConfigValue(preset, 'maxWidth', 1333);

  switch (widthMode) {
    case 'percentage':
      return Math.min(canvasSize.width * (textWidthPercentage / 100), maxWidth);
    case 'adaptive':
      return Math.min(canvasSize.width * 0.8, 1333);
    case 'fixed':
    default:
      return textWidth;
  }
};

/**
 * Calculate font size based on preset config and total character count.
 */
export const calculateFontSize = (preset, totalChars) => {
  const fontSizeMode = getConfigValue(preset, 'fontSizeMode', 'adaptive');

  if (fontSizeMode === 'fixed') {
    return getConfigValue(preset, 'fontSize', DEFAULT_CANVAS_CONFIG.fontSizeBig);
  }

  // Adaptive mode — default rules use full-resolution (v2) pixel values
  const rules = getConfigValue(preset, 'fontSizeRules', [
    { maxChars: 60,    size: 60 },
    { maxChars: 120,   size: 53 },
    { maxChars: 200,   size: 47 },
    { maxChars: 99999, size: 40 },
  ]);

  for (const rule of rules) {
    // Treat null/missing maxChars as Infinity (catch-all last rule)
    const threshold = rule.maxChars == null ? Infinity : rule.maxChars;
    if (totalChars < threshold) {
      return rule.size;
    }
  }

  return rules[rules.length - 1].size;
};

/**
 * Normalize separator spacing. Historical presets may have stored `true`.
 */
export const getSeparatorSpacingMultiplier = (preset) => {
  const rawSpacing = preset?.config?.separatorSpacing;
  if (rawSpacing === true) return 1;
  if (!rawSpacing) return 0;
  return Number(rawSpacing);
};

/**
 * Get bilingual separator layout. Spacing is independent from line visibility;
 * line height is only included when the divider is actually shown.
 */
export const getBilingualSeparatorLayout = (preset, showDivider) => {
  const separatorHeight = getConfigValue(
    preset,
    'separatorHeight',
    DEFAULT_CANVAS_CONFIG.separatorHeight,
  );
  const rawSeparatorPadding = getConfigValue(
    preset,
    'separatorPadding',
    DEFAULT_CANVAS_CONFIG.separatorPadding,
  );
  const separatorPadding =
    rawSeparatorPadding * getSeparatorSpacingMultiplier(preset);

  return {
    separatorHeight,
    separatorPadding,
    visibleSeparatorHeight: showDivider ? separatorHeight : 0,
  };
};

/**
 * Space reserved between two language slots. Text spacing remains active when
 * the optional divider line is hidden.
 */
export const getInterSlotGap = ({
  separatorPadding = 0,
  separatorHeight = 0,
  showDivider = false,
} = {}) =>
  Number(separatorPadding) * 2 + (showDivider ? Number(separatorHeight) : 0);

/**
 * Get font family for special scripts (Cyrillic, Latin Extended).
 */
export const getSpecialFontFamily = (specialFont, defaultFont) => {
  if (specialFont === 'Cyrillic') return DEFAULT_CANVAS_CONFIG.fontFamilyCyrillic;
  if (specialFont === 'LatinExtended') return DEFAULT_CANVAS_CONFIG.fontFamilyLatinExtended;
  return defaultFont;
};

/**
 * Get font size for special scripts.
 */
export const getSpecialFontSize = (specialFont, defaultSize) => {
  if (specialFont === 'Cyrillic') return DEFAULT_CANVAS_CONFIG.fontSizeCyrillic;
  if (specialFont === 'LatinExtended') return DEFAULT_CANVAS_CONFIG.fontSizeLatinExtended;
  return defaultSize;
};

/**
 * Get font style for special scripts.
 */
export const getSpecialFontStyle = (specialFont, defaultStyle) => {
  if (specialFont === 'Cyrillic') return DEFAULT_CANVAS_CONFIG.fontStyleCyrillic;
  if (specialFont === 'LatinExtended') return DEFAULT_CANVAS_CONFIG.fontStyleLatinExtended;
  return defaultStyle;
};

/**
 * Determine special font type based on language code.
 */
export const getSpecialFontForLanguage = (langCode) => {
  if (langCode === 'ru') return 'Cyrillic';
  if (langCode === 'pl' || langCode === 'tr') return 'LatinExtended';
  return '';
};

export const getPresetTextStyle = (
  preset,
  totalChars,
  role = 'primary',
  specialFont = '',
) => {
  const defaultFamily =
    role === 'secondary'
      ? getConfigValue(preset, 'fontFamilySecondary', DEFAULT_CANVAS_CONFIG.fontFamily)
      : getConfigValue(preset, 'fontFamily', DEFAULT_CANVAS_CONFIG.fontFamily);
  const defaultStyle =
    role === 'secondary'
      ? getConfigValue(preset, 'fontStyleSecondary', DEFAULT_CANVAS_CONFIG.fontStyleItalic)
      : getConfigValue(preset, 'fontStyle', DEFAULT_CANVAS_CONFIG.fontStyleNormal);

  const calculatedSize = calculateFontSize(preset, totalChars);

  return {
    fontSize: getSpecialFontSize(specialFont, calculatedSize),
    fontFamily: getSpecialFontFamily(specialFont, defaultFamily),
    fontStyle: getSpecialFontStyle(specialFont, defaultStyle),
  };
};

const createMeasuredText = ({
  text,
  fontSize,
  fontFamily,
  fontStyle,
  width,
  align,
  lineHeight,
}) => {
  return new window.Konva.Text({
    text,
    fontSize,
    fontFamily,
    fontStyle,
    width,
    align,
    lineHeight,
  });
};

export const buildPresetTextNodes = async ({
  text1,
  text2 = '',
  alignment,
  canvasSize,
  preset = null,
  showDivider = false,
  specialFontTop = '',
  specialFontBottom = '',
  centered = false,
  idPrefix = Date.now().toString(),
  source = 'preset-preview',
}) => {
  const textVisibility = getConfigValue(preset, 'textVisibility', 'both');
  const verticalOffset = getConfigValue(preset, 'verticalOffset', 0);
  const verticalAnchor = getConfigValue(preset, 'verticalAnchor', 'block');
  const rawTopText = text1?.trim() || '';
  const rawBottomText = text2?.trim() || '';
  const renderPrimary = textVisibility !== 'secondary';
  const renderSecondary = textVisibility !== 'primary' && !centered;
  const topText = renderPrimary ? rawTopText : '';
  const bottomText = renderSecondary ? rawBottomText : '';
  const hasTopText = Boolean(topText);
  const hasBottomText = Boolean(bottomText);
  const hasRenderedText = hasTopText || hasBottomText;
  const isSingleRenderedText = hasRenderedText && !(hasTopText && hasBottomText);

  if (!hasRenderedText) {
    return { nodes: [], metrics: null };
  }

  await waitForCanvasFonts();

  const lineHeight = getConfigValue(
    preset,
    'lineHeight',
    DEFAULT_CANVAS_CONFIG.textSeparation,
  );
  const textWidth = calculateTextWidth(preset, canvasSize);
  const totalChars = topText.length + bottomText.length;
  const topStyle = getPresetTextStyle(preset, totalChars, 'primary', specialFontTop);
  const bottomStyle = getPresetTextStyle(preset, totalChars, 'secondary', specialFontBottom);
  const padding = getConfigValue(preset, 'padding', DEFAULT_CANVAS_CONFIG.padding);
  const backgroundMode = getConfigValue(preset, 'backgroundMode', 'text');

  const topMeasure = hasTopText
    ? createMeasuredText({
        text: topText,
        ...topStyle,
        width: textWidth,
        align: alignment,
        lineHeight,
      })
    : null;
  const topHeight = topMeasure?.height() || 0;

  const bottomMeasure = hasBottomText
    ? createMeasuredText({
        text: bottomText,
        ...bottomStyle,
        width: textWidth,
        align: alignment,
        lineHeight,
      })
    : null;
  const bottomHeight = bottomMeasure?.height() || 0;

  const {
    separatorHeight,
    separatorPadding,
    visibleSeparatorHeight,
  } = getBilingualSeparatorLayout(preset, showDivider && hasTopText && hasBottomText);

  const interSlotGap = getInterSlotGap({
    separatorPadding,
    separatorHeight,
    showDivider: showDivider && hasTopText && hasBottomText,
  });
  const totalHeight = hasTopText && hasBottomText
    ? topHeight + interSlotGap + bottomHeight
    : topHeight || bottomHeight;
  const commonX = canvasSize.width / 2 - textWidth / 2;
  const blockCenteredY = canvasSize.height / 2 - totalHeight / 2;
  const dividerCenteredY =
    canvasSize.height / 2 - (topHeight + separatorPadding + separatorHeight / 2);
  const centerY =
    verticalAnchor === 'divider' && hasTopText && hasBottomText
      ? dividerCenteredY + verticalOffset
      : blockCenteredY + verticalOffset;

  const nodes = [];

  if (backgroundMode === 'full') {
    nodes.push({
      id: `${idPrefix}-bg`,
      x: 0,
      y: 0,
      width: canvasSize.width,
      height: canvasSize.height,
      type: 'background',
      source,
    });
  } else if (backgroundMode === 'text') {
    nodes.push({
      id: `${idPrefix}-bg`,
      x: commonX - padding.left,
      y: centerY - padding.top,
      width: textWidth + padding.left + padding.right,
      height: totalHeight + padding.top + padding.bottom,
      type: 'background',
      source,
    });
  }

  if (hasTopText) {
    nodes.push({
      id: `${idPrefix}-top`,
      text: topText,
      x: commonX,
      y: centerY,
      width: textWidth,
      align: alignment,
      lineHeight,
      type: 'text',
      padding,
      showBackground: false,
      source,
      ...topStyle,
    });
  }

  if (hasTopText && hasBottomText) {
    const separatorY = centerY + topHeight + separatorPadding;
    nodes.push({
      id: `${idPrefix}-sep`,
      type: 'separator',
      x: commonX,
      y: separatorY,
      width: showDivider ? textWidth : 0,
      height: separatorHeight,
      fill: 'black',
      source,
    });

    nodes.push({
      id: `${idPrefix}-bottom`,
      text: bottomText,
      x: commonX,
      y: separatorY + visibleSeparatorHeight + separatorPadding,
      width: textWidth,
      align: alignment,
      lineHeight,
      type: 'text',
      padding,
      showBackground: false,
      source,
      ...bottomStyle,
    });
  } else if (hasBottomText) {
    nodes.push({
      id: `${idPrefix}-bottom`,
      text: bottomText,
      x: commonX,
      y: centerY,
      width: textWidth,
      align: alignment,
      lineHeight,
      type: 'text',
      padding,
      showBackground: false,
      source,
      ...bottomStyle,
    });
  }

  return {
    nodes,
    metrics: {
      textWidth,
      topHeight,
      bottomHeight,
      totalHeight,
      separatorHeight,
      visibleSeparatorHeight,
      separatorPadding,
      padding,
      lineHeight,
      topStyle,
      bottomStyle,
      backgroundMode,
      textVisibility,
      verticalOffset,
      verticalAnchor,
      isSingleRenderedText,
    },
  };
};
